<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class="tw-flex tw-justify-between tw-items-center">
        <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">Users</h1>
        <base-button @click="openAddUserModal" class="tw-hidden sm:tw-flex">
          <template v-slot:icon>
            <plus-icon />
          </template>
          New User
        </base-button>
        <base-icon @click="openAddUserModal" class="tw-inline sm:tw-hidden">
          <plus-icon class="tw-h-5 tw-w-5" />
        </base-icon>
      </base-content>
    </div>
    <base-content>
      <base-text-field v-model="query" class="w-full" placeholder="Search users" size="md">
        <template #prefix>
          <search-icon class="tw-h-5 tw-w-5" />
        </template>
      </base-text-field>
      <base-table :loading="isLoading" :fields="fields" :items="items" class="tw-mt-4">
        <template #image="{item: user}">
          <div class="tw-h-12 tw-w-12">
            <Avatar :user-id="user.id" avatarType="user" :height="45" :width="45" />
          </div>
        </template>
        <template #user="{item: user}">
          <div class="tw-flex tw-items-center">
            <div>
              <div class="tw-text-base">
                {{ user.fullName }}
              </div>
              <div class="tw-flex">
                <a :href="`mailto:${user.email}`" class="tw-text-xs tw-text-primary-500 tw-block tw-my-auto">{{ user.email }}</a>
                <base-icon class="tw-ml-1 tw-flex-shrink-0" @click="changeEmailModal(user)" color="primary">
                  <edit-icon class="tw-h-3 tw-w-4" />
                </base-icon>
              </div>
            </div>
          </div>
        </template>
        <template #mobile="{item: user}">
          <base-phone-number-field :value="user.mobile" class="tw-text-sm" />
        </template>
        <template #actions="{item: user}">
          <div class="tw-flex tw-flex-nowrap tw-items-center tw-space-x-2">
            <base-button type="flat" size="sm" @click="openEditRolesModal(user)">
              <template v-slot:icon>
                <list-icon />
              </template>
              Roles
            </base-button>
            <base-button type="flat" size="sm" @click="openViewEmailsModal(user)">
              <template v-slot:icon>
                <mail-icon />
              </template>
              Emails
            </base-button>
            <base-button type="flat" size="sm" @click="assume(user)">
              <template v-slot:icon>
                <play-icon />
              </template>
              Assume
            </base-button>
          </div>
        </template>
        <template #empty-state>
          <div class="tw-flex tw-justify-center tw-py-5">
            No Results
          </div>
        </template>
        <template #loading-row>
          <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-12 tw-w-12 tw-rounded-full" /></td>
          <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-3 tw-w-60" /><base-skeleton-loader class="tw-h-2 tw-w-24 tw-mt-2" /></td>
          <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-3 tw-w-32" /></td>
          <td class="tw-py-3 tw-px-4">
            <div class="tw-flex tw-space-x-2">
              <base-skeleton-loader class="tw-h-8 tw-w-20" v-for="key in 3" :key="`action-loader-cell-${key}`" />
            </div>
          </td>
        </template>
      </base-table>
      <base-pagination v-model="page" class="tw-mt-2" />
    </base-content>
    <UserPredictileRoleModal ref="editRolesModal" />
    <NewUserModal ref="newUserModal" @save="loadPage" />
    <change-email ref="changeEmailModal" @save="loadPage" />
    <view-emails-modal ref="viewEmailsModal" />
  </div>
</template>

<script>
import Avatar from "@/components/Avatar.vue";
import UserPredictileRoleModal from "@/views/admin/users/components/UserPredictileRoleModal";
import NewUserModal from "@/views/admin/users/components/NewUserModal";
import ChangeEmail from "@/views/admin/users/components/ChangeEmail";
import ViewEmailsModal from "@/views/admin/users/components/ViewEmailsModal";
import { PlusIcon, SearchIcon, ListIcon, PlayIcon, EditIcon, MailIcon } from "@/assets/svgs";
import { debounce } from "debounce";
import { mapActions } from "vuex";

export default {
  components: {
    Avatar,
    UserPredictileRoleModal,
    NewUserModal,
    ChangeEmail,
    ViewEmailsModal,
    PlusIcon,
    SearchIcon,
    ListIcon,
    PlayIcon,
    EditIcon,
    MailIcon,
  },
  data() {
    return {
      isLoading: true,
      page: {
        take: 50,
        skip: 0,
        total: 0,
      },
      query: null,
      fields: [
        { title: "Image", key: "image", thClass: "tw-w-0 tw-text-center" },
        { title: "User", key: "user" },
        { title: "Mobile", key: "mobile" },
        { title: "Actions", key: "actions", thClass: "tw-w-0 tw-text-center" },
      ],
      items: [],
    };
  },
  created() {
    this.loadPage();
    this.debouncedLoadPage = debounce(this.loadPage, 500);
  },
  methods: {
    ...mapActions("organization", ["changeOrganization"]),
    openAddUserModal() {
      this.$refs.newUserModal.show();
    },
    changeEmailModal(user) {
      this.$refs.changeEmailModal.show(user.id);
    },
    async loadPage() {
      try {
        this.isLoading = true;
        const res = await this.$axios.get(`/users?skip=${this.page.skip}&take=${this.page.take}&query=${encodeURIComponent(this.query || "")}`);
        this.items = res.data.items;
        this.page.take = res.data.take;
        this.page.skip = res.data.skip;
        this.page.total = res.data.total;
      } catch (err) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to load users",
          message: "We had a problem retrieving the list of users. Please reload the page to try again.",
          error: err,
        });
      } finally {
        this.isLoading = false;
      }
    },
    assume(user) {
      this.changeOrganization(user.id);
      this.$router.push(`/users/${user.id}/plans`);
    },
    openEditRolesModal(user) {
      this.$refs.editRolesModal.show(user.id);
    },
    openViewEmailsModal(user) {
      this.$refs.viewEmailsModal.show(user);
    },
  },
  watch: {
    "page.skip"() {
      this.loadPage();
    },
    "page.take"() {
      this.page.skip = 0;
      this.loadPage();
    },
    query() {
      this.page.skip = 0;
      this.debouncedLoadPage();
    },
  },
};
</script>
